import React from 'react';
import { useSelector } from 'react-redux';
import { Modal, message, Dropdown, Button, type MenuProps } from 'antd';
import { ExclamationCircleFilled, MoreOutlined } from '@ant-design/icons';
import { useAtomValue, useSetAtom } from 'jotai';
import { getUserProfile } from '@store/common/selectors';
import { FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR } from '@src/components/summary-panel/FilterDropdown/FilterStatus';
import {
  checkShouldShowApply,
  checkCanEdit,
  checkCanDelete,
  checkCanClone,
  convertToFilterInfo,
  checkCanEnable,
} from '../utils';
import { useAllowCreateFilter, useResetState } from '../../hooks';
import {
  convertCustomTagToDimension,
  gerResponseErrorMessage,
  useCheckSavedFilterInValid,
} from '../../../utils';
import * as atoms from '../../atom';
import type * as Types from '../../../../types';

interface Props {
  savedFilter: Types.SavedFilter;
}

export const Operations = ({ savedFilter }: Props) => {
  const [modal, contextHolder] = Modal.useModal();
  const filterMeta = useAtomValue(atoms.filterMeta);
  const dataSource = useAtomValue(atoms.dataSource);
  const callbacks = useAtomValue(atoms.callbacks);
  const user = useSelector(getUserProfile);
  const { enableApplyAction } = useAtomValue(atoms.controlVariables);
  const disableCreateFilter = !useAllowCreateFilter(dataSource);
  const setAsyncLoadingMask = useSetAtom(atoms.asyncLoadingMask);
  const setFilterEditor = useSetAtom(atoms.filterEditor);
  const setFilterClone = useSetAtom(atoms.filterClone);
  const { reset } = useResetState();
  const checkSavedFilterInValid = useCheckSavedFilterInValid();
  const filterDimensionSet = React.useMemo(
    () =>
      new Set(
        (
          filterMeta.dimensions.map((it) => it.fields).flat(Infinity) as Types.DimensionFieldMeta[]
        ).map((it) => it.value),
      ),
    [filterMeta.dimensions],
  );

  const permissionCheckParams = {
    user,
    savedFilter,
    dataSource,
    disableCreateFilter,
  };
  const allowEdit = checkCanEdit(permissionCheckParams);
  const allowEnable = checkCanEnable(permissionCheckParams);
  const allowDelete = checkCanDelete(permissionCheckParams);
  const allowClone = checkCanClone(permissionCheckParams);

  const handleEdit = async () => {
    if (allowEdit) {
      const convertedRule = convertCustomTagToDimension(filterDimensionSet, savedFilter.rule);
      setFilterEditor(
        convertToFilterInfo({
          ...savedFilter,
          rule: convertedRule,
        }),
      );
    }
  };

  const handleEnable = async () => {
    if (allowEnable) {
      modal.confirm({
        title: `Enable ${savedFilter.name}?`,
        content: `Once you enable the filter, Conviva will start computing the data for the filter. Please note that once you enable a filter, you won't be able to disable it.`,
        onOk: async () => {
          setAsyncLoadingMask(true);
          try {
            await callbacks.onEnableFilter?.(savedFilter.fid, dataSource);
            message.success(`Enable ${savedFilter.name} successfully!`);
          } catch (e: any) {
            const errMsg = gerResponseErrorMessage(e) || `Failed to enable ${savedFilter.name}`;
            message.error(errMsg);
          }
          setAsyncLoadingMask(false);
        },
      });
    }
  };

  const handleDelete = async () => {
    if (allowDelete) {
      modal.confirm({
        title: `Delete ${savedFilter.name}?`,
        icon: <ExclamationCircleFilled />,
        content: `Do you want to delete this filter?`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          setAsyncLoadingMask(true);
          try {
            await callbacks.onDeleteFilter?.([savedFilter.fid], dataSource);
            message.success(`Delete ${savedFilter.name} successfully!`);
          } catch (e: any) {
            const errMsg = gerResponseErrorMessage(e) || `Failed to delete ${savedFilter.name}`;
            message.error(errMsg);
          } finally {
            setAsyncLoadingMask(false);
          }
        },
      });
    }
  };

  const handleClone = () => {
    if (allowClone) {
      const newFilterInfo = convertToFilterInfo(savedFilter);
      newFilterInfo.filter.name = `${newFilterInfo.filter.name} (copy)`;
      newFilterInfo.filter.isHiddenFromCustomer = user.hidden;
      setFilterClone(newFilterInfo);
    }
  };

  const dropdownMenuItems = [
    enableApplyAction &&
      checkShouldShowApply(dataSource, !savedFilter.enabled) && {
        key: 'apply',
        label: 'Apply',
      },
    allowEdit && {
      key: 'edit',
      label: 'Edit',
    },
    allowClone && {
      key: 'clone',
      label: 'Clone',
    },
    allowEnable && {
      key: 'enable',
      label: 'Enable',
    },
    allowDelete && {
      key: 'delete',
      label: 'Delete',
      disabled: !allowEdit,
    },
  ].filter(Boolean);

  if (!dropdownMenuItems.length) return null;

  return (
    <>
      {contextHolder}
      <Dropdown
        className="operations"
        placement="bottomRight"
        trigger={['click']}
        menu={{
          items: dropdownMenuItems as MenuProps['items'],
          onClick: ({ key }) => {
            if (key === 'apply') {
              const { id, filter } = convertToFilterInfo(savedFilter);

              // saved filter including event dimension can't be selected under Session data scope
              if (checkSavedFilterInValid(savedFilter)) {
                message.warning(FIlTER_CONTAINS_INAPPLICALBE_DIMENSION_ERROR);
                return;
              }

              setAsyncLoadingMask(false);
              reset();
              callbacks.onClose?.();

              requestAnimationFrame(() => {
                callbacks.onApplyNotify?.(id, filter, dataSource);
              });
            }

            if (key === 'edit') {
              return handleEdit();
            }

            if (key === 'clone') {
              return handleClone();
            }

            if (key === 'delete') {
              return handleDelete();
            }

            if (key === 'enable') {
              return handleEnable();
            }
          },
        }}
      >
        <Button type="text" icon={<MoreOutlined />} style={{ fontSize: 16 }} />
      </Dropdown>
    </>
  );
};
